import { useAtomValue, useSetAtom } from "jotai";
import { useEffect, useState } from "react";
import { Outlet, useLocation, useNavigate } from "react-router-dom";

import { RelatedDetailProvider } from "@/components/DetailNavigation";
import { cn } from "@/lib/cn";
import { getBreadcrumbs, getExpandedParentNames } from "@/routes/menuConfig";
import { currentUserAtom, menusAtom, resetSessionAtom } from "@/store/auth";

import { SidebarFooter, SidebarLogo, SidebarNav } from "./Sidebar";
import { TopHeader } from "./TopHeader";

/** 后台主布局：侧栏菜单由 getUserMenuTree 接口动态下发 */
export const MainLayout = () => {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const user = useAtomValue(currentUserAtom);
  const menus = useAtomValue(menusAtom);
  const resetSession = useSetAtom(resetSessionAtom);
  // 侧栏折叠态
  const [collapsed, setCollapsed] = useState(false);
  // 展开的父级菜单名
  const [expandedMenus, setExpandedMenus] = useState<string[]>([]);
  const breadcrumbs = getBreadcrumbs(pathname, menus);
  const displayName = user?.nickname || user?.username || "管理员";
  const avatarText = (displayName.charAt(0) || "A").toUpperCase();

  /** 路径变化时自动展开当前所在父级菜单 */
  useEffect(() => {
    const parentNames = getExpandedParentNames(pathname, menus);
    if (parentNames.length === 0) return;
    setExpandedMenus((prev) => {
      const merged = new Set([...prev, ...parentNames]);
      return [...merged];
    });
  }, [pathname, menus]);

  /** 切换父菜单展开 */
  const toggleExpand = (name: string) => {
    setExpandedMenus((prev) =>
      prev.includes(name) ? prev.filter((n) => n !== name) : [...prev, name],
    );
  };

  /** 退出登录：统一 resetSession 清状态 */
  const handleLogout = () => {
    resetSession();
    navigate("/login", { replace: true });
  };

  return (
    <div className="h-screen flex overflow-hidden bg-slate-50/50">
      <aside
        className={cn(
          "h-screen flex flex-col transition-all duration-300 shrink-0 relative z-20",
          "bg-white shadow-[1px_0_8px_-2px_rgba(0,0,0,0.06)]",
          collapsed ? "w-[72px]" : "w-[272px]",
        )}
      >
        <SidebarLogo collapsed={collapsed} onToggleCollapse={() => setCollapsed((v) => !v)} />
        <SidebarNav
          menus={menus}
          pathname={pathname}
          collapsed={collapsed}
          expandedMenus={expandedMenus}
          onToggleExpand={toggleExpand}
        />
        <SidebarFooter collapsed={collapsed} onLogout={() => void handleLogout()} />
      </aside>
      <div className="flex-1 flex flex-col min-w-0 overflow-hidden">
        <TopHeader
          collapsed={collapsed}
          breadcrumbs={breadcrumbs}
          displayName={displayName}
          roleName="超级管理员"
          avatarText={avatarText}
          onToggleCollapse={() => setCollapsed((v) => !v)}
          onLogout={() => void handleLogout()}
        />
        <main className="flex-1 p-5 overflow-hidden bg-slate-50/50 min-h-0">
          <div className="h-full min-h-0">
            <RelatedDetailProvider>
              <Outlet />
            </RelatedDetailProvider>
          </div>
        </main>
      </div>
    </div>
  );
};
